import supabase from "../lib/supabase";

// Brand/profile images render on every portal pageview, so uploads are
// restricted to universally-rasterized formats. The extension is derived
// from the MIME type — never from the user-supplied filename — and the
// content type is set explicitly so the public bucket can only ever serve
// these objects as images.
const ACCEPTED_IMAGE_TYPES: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};
const MAX_IMAGE_SIZE_BYTES = 3 * 1024 * 1024;

export const storageService = {
  async uploadImage(file: File, path: string) {
    try {
      const fileExt = ACCEPTED_IMAGE_TYPES[file.type];
      if (!fileExt) {
        throw new Error(
          "Unsupported image type. Please upload a PNG, JPEG, or WebP image."
        );
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        throw new Error("Image is too large. Maximum size is 3MB.");
      }
      const fileName = `${Date.now()}-${Math.random()}.${fileExt}`;
      const filePath = `${path}/${fileName}`;

      const { error } = await supabase.storage
        .from("reveshare-assets")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
          contentType: file.type,
        });

      if (error) {
        console.error("Upload error:", error);
        throw error;
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from("reveshare-assets").getPublicUrl(filePath);

      if (!publicUrl) {
        throw new Error("Failed to get public URL for uploaded image");
      }

      return publicUrl;
    } catch (error) {
      console.error("Storage service error:", error);
      throw error;
    }
  },

  async uploadMedia(file: File, path: string) {
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}-${Math.random()}.${fileExt}`;
      const filePath = `${path}/${fileName}`;

      const { error } = await supabase.storage
        .from("reveshare-assets")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Upload error:", error);
        throw error;
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from("reveshare-assets").getPublicUrl(filePath);

      if (!publicUrl) {
        throw new Error("Failed to get public URL for uploaded media");
      }

      return publicUrl;
    } catch (error) {
      console.error("Storage service error:", error);
      throw error;
    }
  },

  // Assets for a brand-authored join page: the images the linter lets
  // through plus .woff2 for @font-face. Extension and content type come from
  // the MIME type so the bucket never serves anything as something else.
  async uploadJoinPageAsset(file: File) {
    const JOIN_PAGE_TYPES: Record<string, string> = {
      ...ACCEPTED_IMAGE_TYPES,
      "image/gif": "gif",
      "font/woff2": "woff2",
    };
    // Browsers often report .woff2 with an empty or generic type.
    const type =
      file.type || (file.name.toLowerCase().endsWith(".woff2") ? "font/woff2" : "");
    const fileExt = JOIN_PAGE_TYPES[type];
    if (!fileExt) {
      throw new Error("Use a PNG, JPG, WebP or GIF image, or a .woff2 font.");
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      throw new Error("Files must be 3 MB or smaller.");
    }
    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
    const filePath = `join-page-assets/${fileName}`;
    const { error } = await supabase.storage
      .from("reveshare-assets")
      .upload(filePath, file, {
        cacheControl: "31536000",
        upsert: false,
        contentType: type,
      });
    if (error) {
      console.error("Upload error:", error);
      throw error;
    }
    const {
      data: { publicUrl },
    } = supabase.storage.from("reveshare-assets").getPublicUrl(filePath);
    if (!publicUrl) throw new Error("Failed to get public URL for uploaded asset");
    return publicUrl;
  },

  async uploadProfileImage(file: File) {
    return this.uploadImage(file, "profile-images");
  },

  async uploadCompanyLogo(file: File) {
    return this.uploadImage(file, "company-logos");
  },

  async uploadCompanyBanner(file: File) {
    return this.uploadImage(file, "company-banners");
  },

  async uploadCompanyBannerMobile(file: File) {
    return this.uploadImage(file, "company-banners");
  },

  async uploadShowcaseImage(file: File) {
    return this.uploadImage(file, "showcase-images");
  },

  async uploadSupportMedia(file: File) {
    return this.uploadMedia(file, "support");
  },

  async uploadThankYouBannerLogo(file: File) {
    return this.uploadImage(file, "thank-you-banner-logos");
  },

  async uploadMetaImage(file: File) {
    return this.uploadImage(file, "meta-images");
  },

  /** Affiliate / brand content-program creative uploads (public bucket path) */
  async uploadContentSubmissionMedia(file: File) {
    return this.uploadMedia(file, "content-submissions");
  },

  /** Brand sample content for content programs */
  async uploadProgramSampleMedia(file: File) {
    return this.uploadMedia(file, "program-samples");
  },
};
